import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PackageDetailView, PackageSummaryView, PackageVersionView } from '@lp/contracts';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorText, get, patch, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { PackageContent } from '@/components/package-config';
import { NamesDialog, PriceDialog, PriceText } from '@/components/edit-dialogs';
import { Badge, Button, Card, ErrorBox, Loading, PageHeader } from '@/components/ui';
import { StateBadge } from './clients';
import { PackageStatusBadge } from './packages';

/** one package: what its newest version gives, its versions, and the clients on it */
export function PackagePage() {
  const t = useT();
  const code = (useParams().code ?? '').toUpperCase();
  const nav = useNavigate();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const [viewing, setViewing] = useState<number | null>(null);
  const [naming, setNaming] = useState(false);
  const [pricing, setPricing] = useState(false);
  const detail = useQuery({ queryKey: ['package', code], queryFn: () => get<PackageDetailView>(`/platform/packages/${code}`) });
  const shown = viewing ?? detail.data?.publishedVersion ?? detail.data?.draftVersion ?? null;
  const version = useQuery({ queryKey: ['package', code, 'version', shown], queryFn: () => get<PackageVersionView>(`/platform/packages/${code}/versions/${shown}`), enabled: shown != null });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['package', code] });
    void qc.invalidateQueries({ queryKey: ['packages'] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
  };
  const newDraft = useMutation({ mutationFn: () => post<PackageVersionView>(`/platform/packages/${code}/versions`), onSuccess: (v) => nav(`/packages/${code}/versions/${v.version}/edit`) });
  const status = useMutation({ mutationFn: (s: 'active' | 'archived') => patch<PackageSummaryView>(`/platform/packages/${code}`, { status: s }), onSuccess: refresh });
  const move = useMutation({ mutationFn: (v: number) => post<{ moved: number[]; refused: { clientId: number; code: string; reason: string }[] }>(`/platform/packages/${code}/move-clients`, { version: v }), onSuccess: refresh });

  if (detail.isLoading) return <Loading />;
  if (detail.error || !detail.data) return <ErrorBox text={errorText(detail.error)} />;
  const p = detail.data;
  const behind = p.publishedVersion ? p.clientList.filter((c) => c.version !== p.publishedVersion) : [];

  return (
    <>
      <Link to="/packages" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('All packages')}
      </Link>
      <PageHeader
        title={p.nameEn}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{p.code}</span>
            <span>{p.nameKm}</span>
            <PackageStatusBadge status={p.status} />
            {p.publishedVersion && <Badge>v{p.publishedVersion}</Badge>}
          </span>
        }
        actions={
          superAdmin && (
            <>
              <Button variant="secondary" icon={<Pencil size={14} />} onClick={() => setNaming(true)}>
                {t('Edit')}
              </Button>
              <Button variant="secondary" onClick={() => setPricing(true)}>
                {t('Price')}
              </Button>
              <Button variant="secondary" onClick={() => status.mutate(p.status === 'active' ? 'archived' : 'active')} disabled={status.isPending}>
                {t(p.status === 'active' ? 'Archive' : 'Make active')}
              </Button>
              {p.draftVersion ? (
                <Button icon={<Pencil size={14} />} onClick={() => nav(`/packages/${code}/versions/${p.draftVersion}/edit`)}>
                  {t('Edit draft')} v{p.draftVersion}
                </Button>
              ) : (
                <Button icon={<Plus size={14} />} onClick={() => newDraft.mutate()} disabled={newDraft.isPending}>
                  {t('New version')}
                </Button>
              )}
            </>
          )
        }
      />
      {p.description && <p className="-mt-3 mb-2 text-sm text-ink-600">{p.description}</p>}
      <p className="mb-4 text-sm" data-testid="package-price">
        {t('Price')}: <span className="font-semibold"><PriceText price={p.price} /></span>
      </p>
      {naming && <NamesDialog title={`${t('Edit')} — ${p.code}`} value={p} withDescription onSave={async (v) => { await patch(`/platform/packages/${code}`, v); refresh(); }} onClose={() => setNaming(false)} />}
      {pricing && <PriceDialog title={`${t('Price')} — ${p.code}`} price={p.price} clearLabel={t('No price')} onSave={async (price) => { await patch(`/platform/packages/${code}`, { price }); refresh(); }} onClose={() => setPricing(false)} />}
      {(newDraft.error || status.error) && <ErrorBox text={errorText(newDraft.error ?? status.error)} />}

      <Card
        title={shown ? `${t('Version')} ${shown}${version.data?.status === 'draft' ? ` · ${t('Draft')}` : ''}` : t('What it gives')}
        actions={
          viewing && (
            <Button variant="ghost" onClick={() => setViewing(null)}>
              {t('Back to the published version')}
            </Button>
          )
        }
      >
        {!shown ? <p className="text-sm text-ink-600">{t('No published version yet.')}</p> : version.isLoading ? <Loading /> : version.data ? <PackageContent config={version.data.config} /> : <ErrorBox text={errorText(version.error)} />}
        {version.data?.note && <p className="mt-3 border-t border-ink-100 pt-2 text-xs text-ink-500">{version.data.note}</p>}
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card
          title={`${t('Clients on this package')} (${p.clientList.length})`}
          actions={
            superAdmin &&
            behind.length > 0 && (
              <Button variant="secondary" onClick={() => move.mutate(p.publishedVersion!)} disabled={move.isPending}>
                {t('Move all to')} v{p.publishedVersion} ({behind.length})
              </Button>
            )
          }
        >
          {!p.clientList.length ? (
            <p className="text-sm text-ink-600">{t('No client is on this package.')}</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {p.clientList.map((c) => (
                  <tr key={c.id} className="border-b border-ink-100 last:border-0">
                    <td className="py-2 pr-3">
                      <Link to={`/clients/${c.id}`} className="font-medium text-ink-900 hover:underline">
                        {c.nameEn}
                      </Link>{' '}
                      <span className="font-mono text-[11px] text-ink-500">{c.code}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <StateBadge state={c.state as 'testing'} />
                    </td>
                    <td className="py-2 text-right">
                      <Badge tone={c.version === p.publishedVersion ? 'gray' : 'amber'}>v{c.version}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {move.data && (move.data.refused.length > 0 || move.data.moved.length > 0) && (
            <div className="mt-3 space-y-1 border-t border-ink-100 pt-2 text-xs">
              <p className="text-emerald-700">
                {t('Moved')}: {move.data.moved.length}
              </p>
              {move.data.refused.map((r) => (
                <p key={r.clientId} className="text-red-700">
                  {r.code}: {r.reason}
                </p>
              ))}
            </div>
          )}
          {move.error && <ErrorBox text={errorText(move.error)} />}
        </Card>

        <Card title={t('Versions')}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                <th className="py-1.5 pr-3">{t('Version')}</th>
                <th className="py-1.5 pr-3">{t('Published')}</th>
                <th className="py-1.5 pr-3 text-right">{t('Clients')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {p.versions.map((v) => (
                <tr key={v.version} className="border-b border-ink-100 last:border-0">
                  <td className="py-2 pr-3">
                    v{v.version}{' '}
                    {v.status === 'draft' && (
                      <Badge tone="blue">{t('Draft')}</Badge>
                    )}
                    {v.note && <span className="block text-xs text-ink-500">{v.note}</span>}
                  </td>
                  <td className="py-2 pr-3 text-xs text-ink-500">{dateTime(v.publishedAt)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{v.clients}</td>
                  <td className="py-2 text-right">
                    {v.status === 'draft' && superAdmin ? (
                      <Button variant="ghost" onClick={() => nav(`/packages/${code}/versions/${v.version}/edit`)}>
                        {t('Edit')}
                      </Button>
                    ) : (
                      <Button variant="ghost" onClick={() => setViewing(v.version)}>
                        {t('View')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
